import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import moment from 'moment-timezone'
import {
  Calendar,
  type Field,
  PermissionArea,
  PuiCheckbox,
  PuiTextField,
  useFields,
} from '@pbt/pbt-ui-components'

import AutoshipIcon from '~/components/elements/Icons/generated/AutoshipIcon'
import Typography from '~/components/elements/Typography/Typography'
import { useIsAutoshipEnabled } from '~/store/hooks/business'
import { getCRUDByArea } from '~/store/reducers/auth'
import type { DataHandle } from '~/types'
import { handleNumberInput } from '~/utils'
import { useCurrentBusinessTimezone } from '~/utils/useCurrentBusinessTimezone'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import AutoshipFrequencySelect from './AutoshipFrequencySelect'
import { AutoshipRefillTooltip } from './AutoshipRefillTooltip'

const useStyles = makeStyles(
  (theme) => ({
    inputLabel: {
      width: '100%',
      fontWeight: 400,
    },
    refills: {
      maxWidth: 84,
      marginRight: theme.spacing(3),
    },
    refillExpiration: {
      maxWidth: 136,
    },
    addRefills: {
      display: 'flex',
      backgroundColor: theme.colors.brandLight,
      padding: theme.spacing(1),
      marginBottom: '-8px',
      marginTop: '16px',
    },
    addRefillsText: {
      marginLeft: '5px',
      marginRight: '5px',
    },
    autoshipIcon: {
      opacity: '50%',
    },
  }),
  { name: 'RefillableSection' },
)

export interface RefillableSectionProps {
  autoshipFrequency?: Field
  autoshipUnitId?: Field
  createdAt: string | undefined
  doesPrescriptionHaveInvoiceOrderLineItem?: boolean
  enableAutoship?: Field
  isChewyActiveRx: boolean
  onRefChange?: () => void
  quantityNumbRefills: number | undefined
  quantityRefillExpirationDate: string | undefined
}

export interface RefillableSectionDataHandle extends DataHandle {}

/**
 * Validates that the refill expiration date is within 1 year of the creation date.
 * Uses 364 days to match backend validation (within 1 year, not including day 365).
 */
export const validateRefillExpirationDate = (
  refillExpirationDate: string | undefined,
  creationDate: string | undefined,
  businessTimezone?: string,
) => {
  if (!refillExpirationDate || !creationDate) return true

  // Parses both dates in the same timezone to ensure consistent comparison
  const timezone = businessTimezone || 'UTC'
  const expirationMoment = moment
    .tz(refillExpirationDate, timezone)
    .startOf('day')
  const maxExpirationDate = moment
    .tz(creationDate, timezone)
    .startOf('day')
    .add(364, 'days')

  // Return true if either date is invalid (let other validators handle invalid dates)
  if (!expirationMoment.isValid() || !maxExpirationDate.isValid()) {
    return true
  }
  return expirationMoment.isSameOrBefore(maxExpirationDate)
}

/**
 * Formats date strings for display in the Calendar component.
 * Parses backend ISO dates in business timezone and returns YYYY-MM-DD format (e.g., "2026-01-20").
 */
export const formatDateForDisplay = (
  dateString: string | undefined,
  businessTimezone: string,
): string | undefined => {
  if (!dateString) return undefined

  const parsedDate = moment.tz(dateString, businessTimezone)
  if (!parsedDate.isValid()) return undefined

  return parsedDate.format('YYYY-MM-DD')
}

/**
 * Formats date strings for API submission with proper timezone offset.
 * Returns ISO 8601 format with business timezone offset (e.g., "2026-01-20T00:00:00-08:00").
 */
export const formatDateForSave = (
  dateString: string | undefined,
  businessTimezone: string,
): string | null => {
  if (!dateString) return null

  const parsedDate = moment.tz(dateString, businessTimezone).startOf('day')
  if (!parsedDate.isValid()) return null

  return parsedDate.format()
}

export const RefillableSection = forwardRef<
  RefillableSectionDataHandle,
  RefillableSectionProps
>(function RefillableField(
  {
    quantityNumbRefills: quantityNumbRefillsProp,
    quantityRefillExpirationDate: quantityRefillExpirationDateProp,
    createdAt: creationDate,
    onRefChange,
    isChewyActiveRx,
    autoshipFrequency: autoshipFrequencyField,
    autoshipUnitId: autoshipUnitIdField,
    enableAutoship: enableAutoshipField,
    doesPrescriptionHaveInvoiceOrderLineItem,
  },
  ref,
) {
  const classes = useStyles()
  const { t } = useTranslation('Dialogs')

  const permissions = useSelector(getCRUDByArea(PermissionArea.PRESCRIPTION))
  const isAutoshipEnabled = useIsAutoshipEnabled()
  const businessTimezone = useCurrentBusinessTimezone()

  const [refillable, setRefillable] = useState(Boolean(quantityNumbRefillsProp))

  const eligibleForAutoship =
    !doesPrescriptionHaveInvoiceOrderLineItem &&
    isAutoshipEnabled &&
    isChewyActiveRx &&
    autoshipFrequencyField &&
    autoshipUnitIdField &&
    enableAutoshipField

  const displayAutoshipFrequencySelect = eligibleForAutoship && refillable

  const displayAddAutoshipRefillsBox = eligibleForAutoship && !refillable

  const {
    fields: { quantityNumbRefills, quantityRefillExpirationDate },
    validate,
    reset,
  } = useFields(
    [
      {
        name: 'quantityNumbRefills',
        label: t(
          'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
        ),
        validators: refillable ? ['required'] : [],
        initialValue: quantityNumbRefillsProp,
      },
      {
        name: 'quantityRefillExpirationDate',
        label: t(
          'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILL_EXPIRATION',
        ),
        validators: [
          'timestamp',
          {
            validator: (state) =>
              validateRefillExpirationDate(
                state.value,
                creationDate,
                businessTimezone,
              ),
            validatorName: 'maxOneYear',
          },
        ],
        messages: {
          maxOneYear: t(
            'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILL_EXPIRATION_MAX_ONE_YEAR',
          ),
        },
        initialValue: quantityRefillExpirationDateProp,
      },
    ],
    false,
  )

  useEffect(() => {
    setRefillable(Boolean(quantityNumbRefillsProp))
  }, [quantityNumbRefillsProp])

  useEffectExceptOnMount(() => {
    onRefChange?.()
  }, [quantityNumbRefills, quantityRefillExpirationDate])

  useEffectExceptOnMount(() => {
    reset()
  }, [quantityNumbRefillsProp, quantityRefillExpirationDateProp])

  useEffect(() => {
    if (quantityRefillExpirationDate.value) {
      validate()
    }
  }, [quantityRefillExpirationDate.value])

  useImperativeHandle(ref, () => ({
    validate,
    get: () => {
      // Format date with business timezone offset for API payload
      const expirationDateISO =
        refillable && quantityRefillExpirationDate.value
          ? formatDateForSave(
              quantityRefillExpirationDate.value,
              businessTimezone,
            )
          : null

      return {
        quantityNumbRefills: refillable ? Number(quantityNumbRefills.value) : 0,
        quantityRefillExpirationDate: expirationDateISO,
      }
    },
  }))

  return (
    <Grid container alignItems="center">
      <Grid item mb={-1} mt={2}>
        <PuiCheckbox
          checked={refillable}
          label={t('Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLABLE')}
          onChange={() => setRefillable(!refillable)}
        />
      </Grid>
      {refillable && (
        <>
          <PuiTextField
            InputLabelProps={{
              className: classes.inputLabel,
              shrink: true,
            }}
            className={classes.refills}
            disabled={!permissions.update}
            field={{
              ...quantityNumbRefills,
              set: handleNumberInput(quantityNumbRefills.setValue, 10, 0, true),
            }}
            inputProps={{ maxLength: 10 }}
            label={`${quantityNumbRefills.label}*`}
            margin="none"
          />
          <Grid item>
            <Calendar
              fullWidth
              className={classes.refillExpiration}
              disabled={!permissions.update}
              field={quantityRefillExpirationDate}
              label={quantityRefillExpirationDate.label}
            />
          </Grid>
        </>
      )}
      {displayAddAutoshipRefillsBox && (
        <div className={classes.addRefills}>
          <AutoshipIcon className={classes.autoshipIcon} />
          <Typography.Paragraph
            className={classes.addRefillsText}
            color="grayGray2"
          >
            {t('Soap:AUTOSHIP.ADD_REFILLS_AUTOSHIP')}
          </Typography.Paragraph>
          <AutoshipRefillTooltip />
        </div>
      )}

      {displayAutoshipFrequencySelect && (
        <AutoshipFrequencySelect
          autoshipFrequencyField={autoshipFrequencyField}
          autoshipUnitIdField={autoshipUnitIdField}
          enableAutoshipField={enableAutoshipField}
        />
      )}
    </Grid>
  )
})
